import { useState, useMemo } from 'react'
import { formatCurrency } from '../../lib/format'
import type { CartItem } from '../../hooks/usePosCartChannel'

// ── Tipos ──────────────────────────────────────────

interface Customer {
  id: number
  name: string
  phone?: string
  email?: string
}

interface Props {
  cart: CartItem[]
  subtotal: number
  tax: number
  total: number
  customers: Customer[]
  paymentMethod: string
  selectedCustomer: string
  processing: boolean
  accountSlug: string
  serialNumbers: Record<string, string[]>
  amountReceived: string
  orderNotes: string
  consumptionType: string
  tenantType: string
  onSelectPaymentMethod: (method: string) => void
  onSelectCustomer: (customerId: string) => void
  onCustomerCreated: (customer: Customer) => void
  onSerialNumberChange: (productId: number, value: string[]) => void
  onAmountReceivedChange: (value: string) => void
  onOrderNotesChange: (value: string) => void
  onConsumptionTypeChange: (value: string) => void
  onSubmit: () => void
  onClose: () => void
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo', icon: '💵' },
  { value: 'card', label: 'Tarjeta', icon: '💳' },
  { value: 'transfer', label: 'Transferencia', icon: '🏦' },
  { value: 'other', label: 'Otro', icon: '📱' },
]

export default function PaymentModal({
  cart,
  subtotal,
  tax,
  total,
  customers,
  paymentMethod,
  selectedCustomer,
  processing,
  accountSlug,
  serialNumbers,
  amountReceived,
  orderNotes,
  consumptionType,
  tenantType,
  onSelectPaymentMethod,
  onSelectCustomer,
  onCustomerCreated,
  onSerialNumberChange,
  onAmountReceivedChange,
  onOrderNotesChange,
  onConsumptionTypeChange,
  onSubmit,
  onClose,
}: Props) {
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [newCustomerPhone, setNewCustomerPhone] = useState('')
  const [newCustomerEmail, setNewCustomerEmail] = useState('')
  const [newCustomerRtn, setNewCustomerRtn] = useState('')
  const [creatingCustomer, setCreatingCustomer] = useState(false)

  const formatPrice = (n: number) => formatCurrency(n)

  const changeAmount = useMemo(() => {
    if (paymentMethod !== 'cash' || !amountReceived) return 0
    const received = Math.round(parseFloat(amountReceived.replace(',', '.')) * 100)
    const totalCents = Math.round(total * 100)
    return Math.max(received - totalCents, 0)
  }, [paymentMethod, amountReceived, total])

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) return
    setCreatingCustomer(true)

    try {
      const res = await fetch(`/${accountSlug}/pos/create_customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          name: newCustomerName.trim(),
          phone: newCustomerPhone.trim() || null,
          email: newCustomerEmail.trim() || null,
          tax_id: newCustomerRtn.trim() || null,
        }),
      })
      const data = await res.json()
      if (data.success) {
        onCustomerCreated(data.customer)
        setShowNewCustomer(false)
        setNewCustomerName('')
        setNewCustomerPhone('')
        setNewCustomerEmail('')
        setNewCustomerRtn('')
      } else {
        alert(data.error || 'Error al crear cliente')
      }
    } catch {
      alert('Error de conexión')
    } finally {
      setCreatingCustomer(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !processing) onClose()
      }}
    >
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-[#202020]">Cobrar</h3>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2 disabled:opacity-50"
          >
            ×
          </button>
        </div>

        {/* Resumen */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-500 mb-2">
            {cart.length} {cart.length === 1 ? 'producto' : 'productos'}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="tabular-nums">{formatPrice(subtotal)}</span>
            </div>
            {tax > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">ISV</span>
                <span className="tabular-nums">{formatPrice(tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-[#55ACEE] tabular-nums">
                {formatPrice(total)}
              </span>
            </div>
          </div>
        </div>

        {/* Método de pago */}
        <label className="block text-sm font-medium text-[#202020] mb-2">
          Método de pago
        </label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {PAYMENT_METHODS.map((method) => (
            <button
              key={method.value}
              type="button"
              onClick={() => onSelectPaymentMethod(method.value)}
              disabled={processing}
              className={`px-3 py-2 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                paymentMethod === method.value
                  ? 'border-[#55ACEE] bg-blue-50 text-[#202020] font-medium'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              } disabled:opacity-50`}
            >
              <span className="text-lg">{method.icon}</span>
              <span className="text-sm">{method.label}</span>
            </button>
          ))}
        </div>

        {/* Efectivo recibido — solo en efectivo */}
        {paymentMethod === 'cash' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#202020] mb-2">Efectivo recibido</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amountReceived}
              onChange={(e) => onAmountReceivedChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
            {amountReceived && (
              <p className="text-sm text-gray-600 mt-1">
                Cambio: <span className="font-semibold">{formatPrice(changeAmount / 100)}</span>
              </p>
            )}
          </div>
        )}

        {/* Cliente */}
        <label className="block text-sm font-medium text-[#202020] mb-2">
          Cliente
        </label>
        <div className="flex flex-col sm:flex-row gap-2 mb-2">
          <select
            value={selectedCustomer}
            onChange={(e) => onSelectCustomer(e.target.value)}
            disabled={processing || showNewCustomer}
            className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55ACEE] text-sm disabled:opacity-50"
          >
            <option value="">Consumidor Final</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowNewCustomer((s) => !s)}
            disabled={processing}
            className="whitespace-nowrap px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {showNewCustomer ? '× Cancelar' : '+ Nuevo'}
          </button>
        </div>

        {/* Form para nuevo cliente */}
        {showNewCustomer && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
            <input
              type="text"
              placeholder="Nombre *"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
            <input
              type="tel"
              placeholder="Teléfono"
              value={newCustomerPhone}
              onChange={(e) => setNewCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
            <input
              type="email"
              placeholder="Email"
              value={newCustomerEmail}
              onChange={(e) => setNewCustomerEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
            <input
              type="text"
              placeholder="RTN"
              value={newCustomerRtn}
              onChange={(e) => setNewCustomerRtn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
            <button
              type="button"
              onClick={handleCreateCustomer}
              disabled={!newCustomerName.trim() || creatingCustomer}
              className="w-full px-3 py-2 bg-[#55ACEE] text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
            >
              {creatingCustomer ? 'Creando...' : 'Crear cliente'}
            </button>
          </div>
        )}

        {/* Comentarios de la orden */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#202020] mb-2">Comentarios</label>
          <textarea
            placeholder="Notas de la orden..."
            value={orderNotes}
            onChange={(e) => onOrderNotesChange(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
          />
        </div>

        {/* Tipo de consumo — solo para tenants restaurante */}
        {tenantType === 'restaurante' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#202020] mb-2">Tipo de consumo</label>
          <div className="grid grid-cols-3 gap-2">
            {[['dine_in', 'Comer aquí'], ['takeaway', 'Para llevar'], ['delivery', 'Delivery']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => onConsumptionTypeChange(val)}
                className={`px-2 py-2 rounded-lg border-2 text-sm text-center ${
                  consumptionType === val
                    ? 'border-[#55ACEE] bg-blue-50 font-medium'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Items detalle */}
        <details className="mb-4">
          <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
            Ver detalle ({cart.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-gray-600 max-h-32 overflow-y-auto">
            {cart.map((item) => (
              <li key={item.id} className="flex justify-between">
                <span className="truncate pr-2">
                  {item.quantity}× {item.display_name}
                </span>
                <span className="tabular-nums shrink-0">
                  {formatPrice(item.line_total - (item.discount_cents ?? 0) / 100)}
                </span>
              </li>
            ))}
          </ul>
        </details>

        {/* Números de serie (si hay productos que lo requieren) */}
        {cart.some((item) => item.product?.requires_serial) && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#202020] mb-2">
              🔢 Números de serie
            </label>
            <div className="space-y-3">
              {cart
                .filter((item) => item.product?.requires_serial)
                .map((item) => {
                  // Claves siempre string (backend: product.id.to_s). Guard si prop llega undefined.
                  const productSerials = (serialNumbers ?? {})[String(item.product_id)] || []
                  return (
                    <div key={item.product_id}>
                      <span className="text-xs text-gray-600 block mb-1">
                        {item.product?.name || item.display_name}
                        {item.quantity > 1 && (
                          <span className="text-gray-400"> — {item.quantity} unidad(es)</span>
                        )}
                      </span>
                      <div className="space-y-1">
                        {Array.from({ length: item.quantity }).map((_, i) => (
                          <input
                            key={i}
                            type="text"
                            placeholder={`Serial #${i + 1}`}
                            value={productSerials[i] || ''}
                            onChange={(e) => {
                              const updated = [...productSerials]
                              updated[i] = e.target.value
                              onSerialNumberChange(item.product_id, updated)
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE] font-mono"
                            required
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={
            processing ||
            cart.length === 0 ||
            cart.some((item) => {
              if (!item.product?.requires_serial) return false
              const serials = ((serialNumbers ?? {})[String(item.product_id)] || [])
                .map((s) => s.trim())
                .filter(Boolean)
              return serials.length !== item.quantity
            })
          }
          className="w-full bg-[#FFC107] text-[#202020] py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50"
        >
          {processing ? 'Procesando...' : `Confirmar ${formatPrice(total)}`}
        </button>
      </div>
    </div>
  )
}
